import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { buildPatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'
import { Button, SectionCard } from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as ChartingIcon } from '@enamelai/design-system/icons/Charting'
import ToothChart from '@components/pages/Charting/Dental/ToothChart.component'
import {
  TP_CHART_PANE_MIN_PX,
  TP_CHART_VERTICAL_HANDLE_PX,
  useTreatmentPlanChartVerticalSplit,
} from '@components/pages/Charting/TreatmentPlan/useTreatmentPlanChartVerticalSplit.hook'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import {
  ITreatmentPlan,
  IPhaseItems,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'
import { filterDentalDataByLayers } from '@utils/dental-data.utils'
import {
  getTreatmentPlanReferencedToothIds,
  mergeChartDentalDataWithTreatmentPlanItems,
} from '@utils/treatment-plan.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchChartingbyPatientId } from '@store/actions'
import {
  mockDentalCharts,
  mockTreatmentPlans,
} from '@assets/mockdata/PatientTray/mockTreatmentData'
import { DEMO_CLINICAL_TREATMENT_PLANS } from '@assets/mockdata/PatientTray/mockClinicalSummary'
import ClinicalSummaryTreatmentPlans from './ClinicalSummaryTreatmentPlans.component'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import './PatientProfileClinicalSummary.css'
import '@components/pages/Charting/Dental/ToothChart.css'
import '@components/pages/Charting/TreatmentPlan/TreatmentPlanMain.css'

const CHART_LAYERS: Record<string, boolean> = {
  dental: true,
  perio: false,
  endo: true,
}

/** Same target as Form Builder PatientFormsView — full-screen general loader. */
const CLINICAL_SUMMARY_LOADING_CONTAINER = 'body'
/** Keep loader visible at least this long so it is noticeable on fast/mock loads. */
const CLINICAL_MIN_LOADING_VISIBLE_MS = 1000
type PatientProfileClinicalSummaryProps = {
  patientId: string
  useDemoData?: boolean
  forceEmptyStates?: boolean
}

function resolveActiveDentalChart(
  patientId: string,
  dentalCharts: IDentalChart[],
  selectedDentalChart: IDentalChart | null,
  createDentalChart: IDentalChart,
): IDentalChart | null {
  if (
    selectedDentalChart &&
    selectedDentalChart.chart_id !== createDentalChart.chart_id &&
    selectedDentalChart.patient_id === patientId
  ) {
    return selectedDentalChart
  }

  if (
    createDentalChart.patient_id === patientId &&
    (createDentalChart.dental_data?.length ?? 0) > 0
  ) {
    return createDentalChart
  }

  const patientCharts = dentalCharts.filter(
    (chart) => chart.patient_id === patientId,
  )
  return patientCharts[0] ?? null
}

function filterClinicalTreatmentPlans(
  plans: ITreatmentPlan[],
): ITreatmentPlan[] {
  return plans.filter((plan) => !plan.isDraft)
}

function getVisibleTreatmentPlanItems(
  plan: ITreatmentPlan | null,
): IPhaseItems[] {
  if (!plan?.phases?.length) return []
  return plan.phases
    .filter((phase) => !phase.is_hidden)
    .flatMap((phase) => phase.items?.filter((item) => !item.is_hidden) ?? [])
}

function resolveDefaultActivePlanId(plans: ITreatmentPlan[]): string {
  if (plans.length === 0) return ''
  const inProgressPlan = plans.find((plan) => {
    if (!plan.phases?.length) return true
    return !plan.phases.every(
      (phase) => phase.status === TX_PLAN_STATUS.COMPLETED,
    )
  })
  return (inProgressPlan ?? plans[0]).tp_id
}

function PatientProfileClinicalSummary({
  patientId,
  useDemoData = false,
  forceEmptyStates = false,
}: PatientProfileClinicalSummaryProps): React.ReactElement | null {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { setLoading } = useGlobalLoadingActions()
  const loadingShownAtRef = useRef(0)
  const loadingHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const [isClinicalDataReady, setIsClinicalDataReady] = useState(false)
  const [selectedTooth, setSelectedTooth] = useState<number | null>(null)
  const [activePlanId, setActivePlanId] = useState('')
  const [mockDentalChartsState, setMockDentalChartsState] = useState<
    IDentalChart[]
  >([])
  const [mockTreatmentPlansState, setMockTreatmentPlansState] = useState<
    ITreatmentPlan[]
  >([])
  const dentalCharts = useAppSelector((state) => state.charting.dentalCharts)
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)

  const clearLoadingHideTimeout = useCallback((): void => {
    if (loadingHideTimeoutRef.current) {
      clearTimeout(loadingHideTimeoutRef.current)
      loadingHideTimeoutRef.current = null
    }
  }, [])

  const hideClinicalLoading = useCallback((): void => {
    const elapsed = Date.now() - loadingShownAtRef.current
    const remaining = Math.max(0, CLINICAL_MIN_LOADING_VISIBLE_MS - elapsed)

    clearLoadingHideTimeout()
    loadingHideTimeoutRef.current = setTimeout(() => {
      setLoading(false)
      loadingHideTimeoutRef.current = null
    }, remaining)
  }, [clearLoadingHideTimeout, setLoading])

  useLayoutEffect(() => {
    if (!patientId) return undefined

    loadingShownAtRef.current = Date.now()
    setIsClinicalDataReady(false)
    setLoading(true, { container: CLINICAL_SUMMARY_LOADING_CONTAINER })

    return () => {
      clearLoadingHideTimeout()
      setLoading(false)
    }
  }, [patientId, useDemoData, setLoading, clearLoadingHideTimeout])

  useEffect(() => {
    if (!patientId) {
      setIsClinicalDataReady(false)
      return undefined
    }

    if (useDemoData) {
      setMockDentalChartsState(mockDentalCharts[patientId] ?? [])
      setMockTreatmentPlansState(
        patientId === 'mock-patient-1'
          ? DEMO_CLINICAL_TREATMENT_PLANS
          : (mockTreatmentPlans[patientId] ?? []),
      )
      setIsClinicalDataReady(true)
      return undefined
    }

    let isActive = true
    dispatch(fetchChartingbyPatientId(patientId))
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (isActive) setIsClinicalDataReady(true)
      })

    return () => {
      isActive = false
    }
  }, [patientId, useDemoData, dispatch])

  const isFetchingChartData = !useDemoData && chartingLoading

  const activeDentalChart = useMemo((): IDentalChart | null => {
    if (useDemoData) {
      return mockDentalChartsState[0] ?? null
    }
    return resolveActiveDentalChart(
      patientId,
      dentalCharts,
      selectedDentalChart,
      createDentalChart,
    )
  }, [
    useDemoData,
    mockDentalChartsState,
    patientId,
    dentalCharts,
    selectedDentalChart,
    createDentalChart,
  ])

  const hasChartOnFile =
    !forceEmptyStates &&
    (useDemoData ? mockDentalChartsState.length > 0 : activeDentalChart != null)

  const isClinicalContentReady = isClinicalDataReady && !isFetchingChartData

  useEffect(() => {
    if (!isClinicalContentReady) return undefined

    hideClinicalLoading()

    return clearLoadingHideTimeout
  }, [isClinicalContentReady, hideClinicalLoading, clearLoadingHideTimeout])

  const dentalData = useMemo(() => {
    return activeDentalChart?.dental_data ?? []
  }, [activeDentalChart])

  const clinicalPlans = useMemo((): ITreatmentPlan[] => {
    if (forceEmptyStates) return []
    const source = useDemoData ? mockTreatmentPlansState : treatmentPlans
    return filterClinicalTreatmentPlans(source)
  }, [forceEmptyStates, useDemoData, mockTreatmentPlansState, treatmentPlans])

  useEffect(() => {
    if (clinicalPlans.length === 0) {
      setActivePlanId('')
      return
    }
    setActivePlanId((current) =>
      current && clinicalPlans.some((plan) => plan.tp_id === current)
        ? current
        : resolveDefaultActivePlanId(clinicalPlans),
    )
  }, [clinicalPlans])

  const activePlan = useMemo((): ITreatmentPlan | null => {
    if (!activePlanId) return null
    return clinicalPlans.find((plan) => plan.tp_id === activePlanId) ?? null
  }, [activePlanId, clinicalPlans])

  const activePlanItems = useMemo(
    () => getVisibleTreatmentPlanItems(activePlan),
    [activePlan],
  )

  const chartDentalData = useMemo(() => {
    const merged = mergeChartDentalDataWithTreatmentPlanItems(
      dentalData,
      activePlanItems,
    )
    return filterDentalDataByLayers(merged, CHART_LAYERS)
  }, [dentalData, activePlanItems])

  const treatmentPlanHighlightToothIds = useMemo(() => {
    return getTreatmentPlanReferencedToothIds(activePlanItems)
  }, [activePlanItems])

  const handleActivePlanChange = useCallback((planId: string): void => {
    setActivePlanId(planId)
  }, [])

  const clinicalSplitContainerRef = useRef<HTMLDivElement>(null)
  const {
    chartPaneHeightPx,
    chartPaneRef,
    onHandleMouseDown: onChartVerticalHandleMouseDown,
  } = useTreatmentPlanChartVerticalSplit({
    isSplitActive: true,
    containerRef: clinicalSplitContainerRef,
  })

  const isChartPaneSized = chartPaneHeightPx !== null
  const chartPaneStyle = useMemo((): React.CSSProperties | undefined => {
    if (!isChartPaneSized) return undefined
    return {
      '--tp-chart-pane-height': `${chartPaneHeightPx}px`,
      height: chartPaneHeightPx,
      maxHeight: chartPaneHeightPx,
    } as React.CSSProperties
  }, [chartPaneHeightPx, isChartPaneSized])

  const isLoading = isFetchingChartData

  if (!isClinicalDataReady) return null

  return (
    <div ref={clinicalSplitContainerRef} className="patient-profile-clinical">
      <SectionCard
        title="Patient chart"
        className="patient-profile-section patient-profile-clinical-chart-card"
        headerRight={
          <Button
            variant="primary"
            size="md"
            rightIcon={
              <ArrowRightIcon aria-hidden="true" width={16} height={16} />
            }
            onClick={() =>
              navigate('/charting', {
                state: {
                  previousPage: buildPatientProfilePreviousPage(
                    patientId,
                    'clinical',
                  ),
                },
              })
            }
          >
            View in charting
          </Button>
        }
      >
        <div
          ref={chartPaneRef}
          className={`patient-profile-clinical-chart-wrap${
            isChartPaneSized
              ? ' patient-profile-clinical-chart-wrap--sized'
              : ''
          }`}
          style={chartPaneStyle}
        >
          <div className="patient-profile-clinical-chart-content charting-chart-content">
            {hasChartOnFile ? (
              <ToothChart
                key={patientId}
                dentalData={chartDentalData}
                selectedTooth={selectedTooth}
                setSelectedTooth={setSelectedTooth}
                view="full"
                readOnly
                disableLoadingGate
                allowUpscale={isChartPaneSized}
                treatmentPlanHighlightToothIds={treatmentPlanHighlightToothIds}
              />
            ) : (
              <ProfileEmptyState
                icon={ChartingIcon}
                title="No patient chart"
                description="You don't have any patient chart on file."
              />
            )}
          </div>
        </div>
      </SectionCard>

      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize dental chart"
        aria-valuenow={chartPaneHeightPx ?? TP_CHART_PANE_MIN_PX}
        aria-valuemin={TP_CHART_PANE_MIN_PX}
        tabIndex={0}
        className="treatment-plan-chart-vertical-handle"
        onMouseDown={onChartVerticalHandleMouseDown}
        style={{ flex: `0 0 ${TP_CHART_VERTICAL_HANDLE_PX}px` }}
      >
        <i
          className="mgc_unfold_vertical_line treatment-plan-chart-vertical-handle__icon"
          aria-hidden
        />
      </div>

      <div className="patient-profile-clinical-tx-scroll">
        <ClinicalSummaryTreatmentPlans
          plans={clinicalPlans}
          isLoading={isLoading}
          activePlanId={activePlanId}
          onActivePlanChange={handleActivePlanChange}
        />
      </div>
    </div>
  )
}

export default PatientProfileClinicalSummary
